import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";
import api from "../../../utils/api";

export default function BookDiagnostic() {
  const { labId } = useParams();
  const navigate = useNavigate();
  const [selectedTests, setSelectedTests] = useState([]);
  const [collectionType, setCollectionType] = useState("visit"); // "visit" | "home"
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [forSelf, setForSelf] = useState(true);
  const [member, setMember] = useState({ name: "", age: "" });
  const [paymentMode, setPaymentMode] = useState("offline"); // ✅ default offline

  const { data: labData } = useQuery(["lab-detail", labId], () =>
    api.get(`/patient/labs/${labId}`)
  );

  // ✅ pull patient profile for "Myself" display, same as BookAppointment
  const { data: profileData } = useQuery("patient-profile", () =>
    api.get("/patient/profile")
  );
  const patientName = profileData?.profile?.name ?? "";

  const toggleTest = (testId) =>
    setSelectedTests((prev) =>
      prev.includes(testId) ? prev.filter((t) => t !== testId) : [...prev, testId]
    );

  const bookMutation = useMutation(
    () =>
      api.post("/patient/lab-bookings", {
        labId,
        selectedTests,
        collectionType,
        scheduledDate,
        scheduledTime,
        forMember: !forSelf ? member : null,
        paymentMode,
      }),
    {
      onSuccess: (res) => {
        toast.success("Diagnostic booked!");
        if (paymentMode === "online" && res.paymentUrl) {
          window.location.href = res.paymentUrl;
        } else {
          navigate("/patient/booking-history");
        }
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const lab = labData?.lab;

  return (
    <div className="max-w-2xl space-y-5">
      {lab && (
        <div className="card flex items-center gap-4">
          <div className="text-4xl">🧪</div>
          <div>
            <h2 className="font-display font-semibold text-slate-800 text-lg">{lab.name}</h2>
            <p className="text-slate-500 text-sm">{lab.address}</p>
          </div>
        </div>
      )}

      {/* Test Selection */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">1. Select Tests</h3>
        <div className="space-y-2">
          {(lab?.tests || []).map((test) => (
            <label key={test.id} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
              <input
                type="checkbox"
                checked={selectedTests.includes(test.id)}
                onChange={() => toggleTest(test.id)}
              />
              <span className="flex-1 text-sm text-slate-700">{test.name}</span>
              <span className="text-sm font-semibold text-slate-800">₹{test.price}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Collection Type */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">2. Collection Type</h3>
        <div className="flex gap-4">
          {[
            { key: "visit", label: "Lab Visit" },
            { key: "home", label: "Home Collection" },
          ].map((opt) => (
            <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={collectionType === opt.key}
                onChange={() => setCollectionType(opt.key)}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Schedule */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">3. Schedule</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Date</label>
            <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="input-field" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Time</label>
            <input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="input-field" />
          </div>
        </div>
      </div>

      {/* Booking For */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">4. Booking For</h3>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={forSelf} onChange={() => setForSelf(true)} />
            <span className="text-sm">Myself</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={!forSelf} onChange={() => setForSelf(false)} />
            <span className="text-sm">Someone else</span>
          </label>
        </div>

        {forSelf && patientName && (
          <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-100 rounded-lg w-fit">
            <span className="text-lg">👤</span>
            <span className="text-sm font-medium text-primary-700">{patientName}</span>
          </div>
        )}

        {!forSelf && (
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Name"
              value={member.name}
              onChange={(e) => setMember((m) => ({ ...m, name: e.target.value }))}
              className="input-field"
            />
            <input
              type="number"
              placeholder="Age"
              value={member.age}
              onChange={(e) => setMember((m) => ({ ...m, age: e.target.value }))}
              className="input-field"
            />
          </div>
        )}
      </div>

      {/* Payment */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">5. Payment</h3>
        <div className="flex gap-4">
          {/* ✅ Online disabled, offline always selected — matches BookAppointment */}
          <label className="flex items-center gap-2 cursor-not-allowed opacity-40">
            <input type="radio" checked={false} disabled />
            <span className="text-sm">Online</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={true} readOnly />
            <span className="text-sm">Offline</span>
          </label>
        </div>
      </div>

      <button
        onClick={() => bookMutation.mutate()}
        disabled={selectedTests.length === 0 || !scheduledDate || bookMutation.isLoading}
        className="btn-primary w-full text-base py-3"
      >
        {bookMutation.isLoading ? "Booking…" : "Confirm Booking"}
      </button>
    </div>
  );
}